import { useEffect } from "react";
import type { UseQueryResult } from "react-query";
import type { AxiosResponse, AxiosError } from "axios";
import memoize from "memoizee";
import debounce from "lodash/debounce";

import activities from "../../../../redux/activities";
import { OperationResponse, Operation } from "../../../../models/operations";
import {
  QueryFunction,
  FetcherQueryKey,
  registerOnQueryRemoveCallback,
} from "../../../../services/query";
import { ensureValue } from "../../../../services/util";
import operationsService from "../../../../services/operations";
import { refinedOperation_NOT_MEMOIZED } from "../../../../services/helpers/refinements";

import { useClearCurrentOperationId } from "../currentOperationId";
import { useInvalidatePreOperations } from "../preOperations";
import type { ResultOfUseSetCurrentOperationDataGlobalActivityAndStatus } from "../currentOperation/currentOperation.globalStatus";

export type OperationFetcherParams = {
  operationId: string | undefined;
  userId: string | undefined;
  catalogFlag: boolean | undefined;
};
export type OperationQueryKey = FetcherQueryKey<OperationFetcherParams>;

export const operationQueryKeyPrefix = "[@fabric/core][operation]";

/**
 * Computes the operationQueryKey from the given params.
 */
export const computeOperationQueryKey = (
  params: OperationFetcherParams,
): OperationQueryKey => [operationQueryKeyPrefix, params];

/**
 * Fetches the operation using the operationsService.
 */
export const operationFetcher: QueryFunction<
  AxiosResponse<OperationResponse>,
  OperationFetcherParams
> = (context) => {
  const operationId = ensureValue(
    context.queryKey[1].operationId,
    "operationId",
  );
  const catalogFlag = ensureValue(
    context.queryKey[1].catalogFlag,
    "catalogFlag",
  );
  return operationsService.getOperationResponse(operationId, catalogFlag);
};

/**
 * Compute the data from the response.
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 *
 * This memoization is cleaned up by useCleanupMemoization.
 */
export const computeDataFromResponse = memoize(refinedOperation_NOT_MEMOIZED, {
  profileName: "[operation.query.helpers] computeDataFromResponse",
});

/**
 * Compute the result of useQuery,
 * refining the response data to an operation.
 *
 * This is only for use in useOperationQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<
    AxiosResponse<OperationResponse>,
    AxiosError
  >,
): UseQueryResult<Operation, AxiosError> => {
  const operationResponse = resultOfUseQuery.data;

  // Convert the response object into the expected format.
  const operation = operationResponse
    ? computeDataFromResponse(operationResponse.data)
    : undefined;

  return {
    ...resultOfUseQuery,
    data: operation,
  } as UseQueryResult<Operation, AxiosError>;
};

/**
 * The returned value of this will contain
 * the React Query onSuccess function for useOperationQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useOperationQuery.
 *
 * This is only meant for use in useOperationQuery.
 */
export const onSuccess = debounce(
  ({
    operationId,
    currentOperationId,
    setCurrentOperationDataGlobalActivityAndStatus,
  }: {
    operationId: string | undefined;
    currentOperationId: string | undefined;
    setCurrentOperationDataGlobalActivityAndStatus: ResultOfUseSetCurrentOperationDataGlobalActivityAndStatus;
  }) => {
    if (operationId === currentOperationId) {
      setCurrentOperationDataGlobalActivityAndStatus({
        activity: activities.setCurrentOperation,
        status: "success",
      });
    }
  },
  0,
);

/**
 * The returned value of this will contain
 * the React Query onError function for useOperationQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useOperationQuery.
 *
 * This function has to be debounced or the promises will step on eachother
 * and cause a race condition that causes app crashes.
 *
 * This is only meant for use in useOperationQuery.
 */
export const onError = debounce(
  async ({
    error,
    operationId,
    currentOperationId,
    invalidatePreOperations,
    clearCurrentOperationId,
    setCurrentOperationDataGlobalActivityAndStatus,
  }: {
    error: AxiosError;
    operationId: string | undefined;
    currentOperationId: string | undefined;
    invalidatePreOperations: ReturnType<typeof useInvalidatePreOperations>;
    clearCurrentOperationId: ReturnType<typeof useClearCurrentOperationId>;
    setCurrentOperationDataGlobalActivityAndStatus: ResultOfUseSetCurrentOperationDataGlobalActivityAndStatus;
  }) => {
    if (operationId === currentOperationId) {
      // If our current operation has been deleted, we need to
      // clear the current operation id and determine a new one.
      //
      // TODO: We probably want to display some sort of message
      // to the user here like:
      // "This operation has been deleted. Rerouting to a valid operation"
      if (error.response?.status === 404) {
        await invalidatePreOperations();
        await clearCurrentOperationId();
      }

      setCurrentOperationDataGlobalActivityAndStatus({
        activity: activities.setCurrentOperation,
        status: "error",
      });
    }
  },
  0,
);

/**
 * The returned value of this will contain
 * the function to call in useOperationQuery when loading.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useOperationQuery.
 *
 * This is only meant for use in useOperationQuery.
 */
export const onLoading = debounce(
  ({
    operationId,
    currentOperationId,
    setCurrentOperationDataGlobalActivityAndStatus,
  }: {
    operationId: string | undefined;
    currentOperationId: string | undefined;
    setCurrentOperationDataGlobalActivityAndStatus: ResultOfUseSetCurrentOperationDataGlobalActivityAndStatus;
  }) => {
    if (operationId === currentOperationId) {
      setCurrentOperationDataGlobalActivityAndStatus({
        activity: activities.setCurrentOperation,
        status: "loading",
      });
    }
  },
  0,
);

/**
 * This hook contains the logic to run in useOperationQuery
 * when loading.
 *
 * This hook is only meant for use in useOperationQuery.
 */
export const useHandleLoadingStatus = ({
  operationId,
  currentOperationId,
  resultOfUseQuery,
  setCurrentOperationDataGlobalActivityAndStatus,
}: {
  operationId: string | undefined;
  currentOperationId: string | undefined;
  resultOfUseQuery: UseQueryResult;
  setCurrentOperationDataGlobalActivityAndStatus: ResultOfUseSetCurrentOperationDataGlobalActivityAndStatus;
}): void => {
  const isStatusLoading = resultOfUseQuery.status === "loading";

  // Why a useEffect? So that this only sets the current operation global
  // activity and status when these values change, as opposed to on
  // every render when we happen to be loading. This is a pain but is only
  // necessary to do while we preserve the behavior the deprecated data hooks.
  useEffect(() => {
    if (isStatusLoading) {
      onLoading({
        operationId,
        currentOperationId,
        setCurrentOperationDataGlobalActivityAndStatus,
      });
    }
  }, [
    operationId,
    currentOperationId,
    isStatusLoading,
    setCurrentOperationDataGlobalActivityAndStatus,
  ]);
};

/**
 * This hook contains the logic to run in useOperationQuery
 * to cleanup any global memoization on query removal.
 *
 * This hook is only meant for use in useOperationQuery.
 */
export const useCleanupMemoization = (queryKey: OperationQueryKey) => {
  useEffect(() => {
    registerOnQueryRemoveCallback({
      queryKey,
      onRemove: (data: AxiosResponse<OperationResponse>) => {
        computeDataFromResponse.delete(data.data);
      },
    });
  }, [queryKey]);
};

/**
 * The returned value of this will contain
 * the React Query isDataEqual function for useOperationQuery.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in useOperationQuery.
 */
export const isDataEqual = (
  prevResponse: AxiosResponse<OperationResponse> | undefined,
  response: AxiosResponse<OperationResponse>,
): boolean => {
  if (!prevResponse) {
    return false;
  }

  return (
    prevResponse.data.modify_date === response.data.modify_date &&
    prevResponse.data.user_role === response.data.user_role
  );
};
